import { useEffect, useMemo, useRef, useState } from "react"
import { Plus, Save, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { OdooCrmCard } from "@/components/OdooCrmCard"
import { AreaAccessTab } from "@/components/settings/AreaAccessTab"
import { SiteKitsTab } from "@/components/settings/SiteKitsTab"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  fetchSettings,
  listBrands,
  type BrandRow,
  patchSettings,
  paymentTermsValid,
  FALLBACK_PAYMENT_TERMS,
  type ModuleConfig,
  type OdooCrmConfig,
  type PaymentTermsPreset,
  type SettingsRow,
} from "@/lib/api"
import { PaymentTermsLines } from "@/components/PaymentTermsLines"
import { TiptapEditor } from "@/components/TiptapEditor"
import { cn } from "@/lib/utils"

/** true quando o doc Tiptap não tem texto (só parágrafo vazio ou nada). */
function isEmptyDoc(doc: unknown): boolean {
  if (!doc || typeof doc !== "object") return true
  const content = (doc as { content?: unknown[] }).content
  if (!Array.isArray(content) || content.length === 0) return true
  const text = JSON.stringify(content).match(/"text":"([^"]*)"/g) ?? []
  return text.every((t) => t.replace(/"text":"|"/g, "").trim() === "")
}
import { BrandMarginsTab } from "@/components/settings/BrandMarginsTab"
import { CategoryRolesTab } from "@/components/settings/CategoryRolesTab"
import { ContractedPowersTab } from "@/components/settings/ContractedPowersTab"
import { KIND_LABEL_PT } from "@/lib/kind-labels"

interface SettingsProps {
  shell: AppShell | null
}

type NumericKey = keyof Pick<
  SettingsRow,
  | "default_panel_power_w"
  | "min_panels"
  | "max_panels"
  | "coverage_with_battery"
  | "coverage_without_battery"
  | "production_per_kwp_year"
  | "default_brand_id"
  | "site_kit_brand_id"
  | "angle_flat"
  | "angle_low"
  | "angle_high"
  | "default_margin"
  | "vat_multiplier"
  | "tax_exemption_factor"
  | "max_discount_fraction"
  | "battery_discount_min_qty"
  | "battery_discount_fraction"
  | "summer_start_month"
  | "summer_end_month"
  | "summer_empty_consumption_factor"
  | "sell_price_empty_eur_kwh"
  | "sell_price_peak_eur_kwh"
  | "sell_price_full_eur_kwh"
  | "default_consumption_empty_fraction"
  | "default_consumption_peak_fraction"
  | "default_consumption_full_fraction"
  | "tariff_simple_price_eur_kwh"
  | "tariff_bi_empty_eur_kwh"
  | "tariff_bi_peak_eur_kwh"
  | "tariff_bi_full_eur_kwh"
  | "tariff_tri_empty_eur_kwh"
  | "tariff_tri_peak_eur_kwh"
  | "tariff_tri_full_eur_kwh"
  | "pvgis_loss_pct"
  | "auto_create_default_bill_eur"
  | "estimate_fixed_cost_fraction"
>

type StringKey = keyof Pick<
  SettingsRow,
  | "pvgis_pvtechchoice"
  | "pdf_company_name"
  | "pdf_company_logo_url"
  | "pdf_footer_text"
  | "auto_create_tag"
>

type ModuleConfigKey = "ac_config" | "bc_aqs_config" | "bc_aq_config"

export function Settings({ shell }: SettingsProps) {
  const [settings, setSettings] = useState<SettingsRow | null>(null)
  const [draft, setDraft] = useState<Partial<SettingsRow>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Documento Tiptap (ProseMirror JSON) das condições / modelo de contrato.
  const [contractDoc, setContractDoc] = useState<unknown | null>(null)
  const initialContractDoc = useRef<string>("null")

  async function reload() {
    if (!shell) {
      setLoading(false)
      setError(
        "Sem shell — esta página requer a app instalada num tenant KOORTEX.",
      )
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchSettings(shell)
      setSettings(data)
      setDraft({})
      const doc = data.pdf_contract_model_json ?? null
      setContractDoc(doc)
      initialContractDoc.current = JSON.stringify(doc)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha a carregar")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell])

  const isDirty = useMemo(() => {
    if (Object.keys(draft).length > 0) return true
    return JSON.stringify(contractDoc) !== initialContractDoc.current
  }, [draft, contractDoc])

  function getValue<K extends keyof SettingsRow>(key: K): SettingsRow[K] | undefined {
    if (key in draft) return draft[key] as SettingsRow[K]
    return settings ? settings[key] : undefined
  }

  function setNumeric(key: NumericKey, raw: string) {
    if (raw === "" || raw === "-") {
      setDraft((d) => ({ ...d, [key]: null as never }))
      return
    }
    const n = Number(raw)
    if (Number.isFinite(n)) {
      setDraft((d) => ({ ...d, [key]: n as never }))
    }
  }

  function setString(key: StringKey, raw: string) {
    setDraft((d) => ({ ...d, [key]: (raw === "" ? null : raw) as never }))
  }

  // Distribuição de consumos: guardada em fracção (0.30) mas apresentada em %
  // (30). pctOf lê fracção→%, setPct escreve %→fracção.
  function pctOf(key: NumericKey): number | undefined {
    const v = getValue(key)
    return v == null ? undefined : Math.round(Number(v) * 100)
  }
  function setPct(key: NumericKey, raw: string) {
    setNumeric(key, raw === "" ? "" : String((Number(raw) || 0) / 100))
  }

  // ── Condições de pagamento (presets) ────────────────────────────────
  const presets: PaymentTermsPreset[] =
    (getValue("payment_terms_presets") as PaymentTermsPreset[] | null | undefined) ??
    [{ name: "30/70", lines: FALLBACK_PAYMENT_TERMS }]

  function setPresets(next: PaymentTermsPreset[]) {
    setDraft((d) => ({ ...d, payment_terms_presets: next }))
  }

  function patchPreset(index: number, patch: Partial<PaymentTermsPreset>) {
    setPresets(presets.map((p, i) => (i === index ? { ...p, ...patch } : p)))
  }

  function addPreset() {
    setPresets([
      ...presets,
      { name: "", lines: [{ pct: 50, label: "Adjudicação" }, { pct: 50, label: "Instalação" }] },
    ])
  }

  function removePreset(index: number) {
    setPresets(presets.filter((_, i) => i !== index))
  }

  function getModuleConfig(key: ModuleConfigKey): ModuleConfig {
    if (key in draft) return (draft[key] as ModuleConfig | null) ?? {}
    return (settings?.[key] as ModuleConfig | null) ?? {}
  }

  function patchModuleConfig(key: ModuleConfigKey, patch: Partial<ModuleConfig>) {
    const current = getModuleConfig(key)
    const next: ModuleConfig = { ...current, ...patch }
    // Limpa chaves com `null` para enxugar o jsonb persistido.
    for (const [k, v] of Object.entries(next)) {
      if (v == null) delete next[k]
    }
    setDraft((d) => ({ ...d, [key]: next }))
  }

  async function save() {
    if (!shell || !isDirty) return
    // Gate: cada preset precisa de nome e de percentagens a somar 100%.
    if (draft.payment_terms_presets) {
      const bad = draft.payment_terms_presets.find(
        (p) => !p.name.trim() || !paymentTermsValid(p.lines) ||
          p.lines.some((l) => !l.label.trim()),
      )
      if (bad) {
        shell.bridge.toast({
          variant: "error",
          description: `Condições de pagamento "${bad.name || "sem nome"}": nome, descrições e soma 100% obrigatórios`,
        })
        return
      }
    }
    // Gate: distribuição de consumos tem de somar 100% (tolerância de 1%).
    const distSum =
      (Number(getValue("default_consumption_empty_fraction")) || 0) +
      (Number(getValue("default_consumption_peak_fraction")) || 0) +
      (Number(getValue("default_consumption_full_fraction")) || 0)
    if (Math.abs(distSum - 1) > 0.011) {
      shell.bridge.toast({
        variant: "error",
        description: `Distribuição de tarifas tem de somar 100% (está em ${Math.round(distSum * 100)}%)`,
      })
      return
    }
    setSaving(true)
    try {
      const patch: Partial<SettingsRow> = { ...draft }
      if (JSON.stringify(contractDoc) !== initialContractDoc.current) {
        // Doc vazio (sem parágrafos com texto) → null.
        patch.pdf_contract_model_json = isEmptyDoc(contractDoc) ? null : contractDoc
      }
      const updated = await patchSettings(shell, patch)
      setSettings(updated)
      setDraft({})
      const doc = updated.pdf_contract_model_json ?? null
      setContractDoc(doc)
      initialContractDoc.current = JSON.stringify(doc)
      shell.bridge.toast({
        variant: "success",
        description: "Configurações guardadas",
      })
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a guardar",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading)
    return <p className="text-sm text-muted-foreground">A carregar configurações…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!settings) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Configurações</h1>
          <p className="text-sm text-muted-foreground">
            Parâmetros comuns e por módulo (Solar / AC / Bombas de calor).
          </p>
        </div>
        <Button onClick={save} disabled={!isDirty || saving}>
          <Save className="h-4 w-4" />
          {saving ? "A guardar…" : "Guardar alterações"}
        </Button>
      </div>

      <Tabs defaultValue="common">
        <TabsList>
          <TabsTrigger value="common">Comum</TabsTrigger>
          <TabsTrigger value="solar">Solar</TabsTrigger>
          <TabsTrigger value="ac">{KIND_LABEL_PT.ac}</TabsTrigger>
          <TabsTrigger value="bc_aqs">{KIND_LABEL_PT.bc_aqs}</TabsTrigger>
          <TabsTrigger value="bc_aq">{KIND_LABEL_PT.bc_aq}</TabsTrigger>
          {shell?.isAdmin && <TabsTrigger value="site_kits">Kits do site</TabsTrigger>}
          {shell?.isAdmin && <TabsTrigger value="areas">Acesso por área</TabsTrigger>}
        </TabsList>

        {/* ============================================================ */}
        {/* COMUM                                                          */}
        {/* ============================================================ */}
        <TabsContent value="common">
          <div className="space-y-4">

            <Card>
              <CardHeader>
                <CardTitle>Condições de Pagamento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Presets disponíveis ao criar/editar propostas. O{" "}
                  <strong>primeiro</strong> é aplicado por omissão quando a
                  simulação é gerada. As percentagens de cada preset têm de
                  somar 100%.
                </p>
                {presets.map((preset, pi) => (
                  <div
                    key={pi}
                    className="space-y-3 rounded-md border border-border p-3"
                  >
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">
                          Nome do preset
                          {pi === 0 && (
                            <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                              default
                            </span>
                          )}
                        </Label>
                        <Input
                          value={preset.name}
                          placeholder="Ex.: 30/70"
                          onChange={(e) =>
                            patchPreset(pi, { name: e.target.value })
                          }
                          className="h-9"
                        />
                      </div>
                      <button
                        type="button"
                        aria-label="Remover preset"
                        disabled={presets.length <= 1}
                        onClick={() => removePreset(pi)}
                        className="mb-1 rounded p-2 text-destructive hover:bg-destructive/10 disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <PaymentTermsLines
                      lines={preset.lines}
                      onChange={(lines) => patchPreset(pi, { lines })}
                    />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addPreset}>
                  <Plus className="h-4 w-4" />
                  Adicionar preset
                </Button>
              </CardContent>
            </Card>

            <OdooCrmCard
              shell={shell}
              value={(("odoo_crm_config" in draft ? draft.odoo_crm_config : settings?.odoo_crm_config) ?? {}) as OdooCrmConfig}
              onChange={(next) => setDraft((d) => ({ ...d, odoo_crm_config: next }))}
            />

            <Card>
              <CardHeader>
                <CardTitle>Email da proposta</CardTitle>
                <p className="text-sm text-muted-foreground">
                  O email abre com a saudação, dois parágrafos escritos pela IA à medida da proposta e o que ela inclui (✅, a partir
                  dos dados). Aqui fica o texto fixo que se segue — apresentação da empresa e o que o cliente tem sem custo — e por
                  fim a assinatura de email do comercial (a da conta no Koortex). Vazio = o texto por omissão.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {(["solar", "ac", "bc"] as const).map((k) => {
                  const texts = (("proposal_email_texts" in draft ? draft.proposal_email_texts : settings?.proposal_email_texts) ?? {}) as Record<string, string | undefined>
                  return (
                    <div key={k} className="space-y-1">
                      <Label>{k === "solar" ? "Solar" : k === "ac" ? "Ar Condicionado" : "Bombas de calor"}</Label>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        placeholder={k === "solar"
                          ? "Vazio = apresentação da +ENERGIA (22 anos, DGEG, CERTIF, PME Líder) e as vantagens sem custo (monitorização, DGEG/E-REDES, venda do excedente…)."
                          : "Vazio = apresentação da +ENERGIA (22 anos, DGEG, CERTIF, PME Líder)."}
                        value={texts[k] ?? ""}
                        onChange={(e) => {
                          const next = { ...texts, [k]: e.target.value }
                          if (!e.target.value.trim()) delete next[k]
                          setDraft((d) => ({ ...d, proposal_email_texts: next }))
                        }}
                      />
                    </div>
                  )
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>PDF — Proposta (defaults)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Grid>
                  <StrField
                    label="Nome da empresa"
                    value={getValue("pdf_company_name") ?? ""}
                    onChange={(v) => setString("pdf_company_name", v)}
                  />
                  <StrField
                    label="URL do logo (PNG/JPG)"
                    value={getValue("pdf_company_logo_url") ?? ""}
                    onChange={(v) => setString("pdf_company_logo_url", v)}
                  />
                  <StrField
                    label="Rodapé (texto)"
                    value={getValue("pdf_footer_text") ?? ""}
                    onChange={(v) => setString("pdf_footer_text", v)}
                  />
                </Grid>
                <Separator />
                <div>
                  <Label className="mb-1 block">
                    Condições gerais / Modelo de contrato
                  </Label>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Este texto aparece na página "Condições gerais" do PDF.
                  </p>
                  <TiptapEditor value={contractDoc} onChange={setContractDoc} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* SOLAR                                                          */}
        {/* ============================================================ */}
        <TabsContent value="solar">
          <Tabs defaultValue="system">
            <TabsList>
              <TabsTrigger value="system">Sistema FV</TabsTrigger>
              <TabsTrigger value="angles">Inclinações</TabsTrigger>
              <TabsTrigger value="margins">Margens Solar</TabsTrigger>
              <TabsTrigger value="summer">Verão</TabsTrigger>
              <TabsTrigger value="sell">Venda à rede</TabsTrigger>
              <TabsTrigger value="tariffs">Tarifas</TabsTrigger>
              <TabsTrigger value="pvgis">PVGIS</TabsTrigger>
              <TabsTrigger value="brands">Marcas</TabsTrigger>
              <TabsTrigger value="categories">Categorias → Roles</TabsTrigger>
              <TabsTrigger value="powers">Potências</TabsTrigger>
              <TabsTrigger value="autogen">Auto-geração</TabsTrigger>
            </TabsList>

            <TabsContent value="system">
              <Card>
                <CardHeader>
                  <CardTitle>Sistema Fotovoltaico</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Potência do painel default (W)"
                      value={getValue("default_panel_power_w")}
                      onChange={(v) => setNumeric("default_panel_power_w", v)}
                    />
                    <NumField
                      label="Qtd. mínima de painéis"
                      value={getValue("min_panels")}
                      onChange={(v) => setNumeric("min_panels", v)}
                    />
                    <NumField
                      label="Qtd. máxima de painéis"
                      value={getValue("max_panels")}
                      onChange={(v) => setNumeric("max_panels", v)}
                    />
                    <NumField
                      label="Cobertura com bateria (fração)"
                      helper="% do consumo anual a cobrir quando há bateria. Ex.: 0.80 = 80% → mais painéis."
                      value={getValue("coverage_with_battery") ?? ""}
                      onChange={(v) => setNumeric("coverage_with_battery", v)}
                    />
                    <NumField
                      label="Cobertura sem bateria (fração)"
                      helper="% do consumo anual a cobrir sem bateria (autoconsumo directo). Ex.: 0.50 = 50% → menos painéis."
                      value={getValue("coverage_without_battery") ?? ""}
                      onChange={(v) => setNumeric("coverage_without_battery", v)}
                    />
                    <NumField
                      label="Produção por kWp (kWh/ano)"
                      helper="Produção típica anual por kWp em PT. Default 1500."
                      value={getValue("production_per_kwp_year") ?? ""}
                      onChange={(v) => setNumeric("production_per_kwp_year", v)}
                    />
                    <NumField
                      label="Custos fixos da fatura (fracção)"
                      helper="Retirado ao valor da conta antes de estimar kWh (potência/taxas). Ex.: 0.26 = 26%."
                      step="0.01"
                      value={getValue("estimate_fixed_cost_fraction") ?? ""}
                      onChange={(v) => setNumeric("estimate_fixed_cost_fraction", v)}
                    />
                    <BrandSelectField
                      shell={shell}
                      label="Marca por omissão"
                      helper="A marca que o simulador usa quando a proposta não escolhe outra."
                      value={getValue("default_brand_id")}
                      onChange={(v) => setNumeric("default_brand_id", v)}
                    />
                    <BrandSelectField
                      shell={shell}
                      label="Marca dos kits do site"
                      helper="Pedidos do site com um kit que ainda não tem proposta-modelo (ex.: Growatt). «Nenhuma» = a marca por omissão."
                      value={getValue("site_kit_brand_id")}
                      onChange={(v) => setNumeric("site_kit_brand_id", v)}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="angles">
              <Card>
                <CardHeader>
                  <CardTitle>Inclinações do telhado</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Ângulo — plano (°)"
                      value={getValue("angle_flat")}
                      onChange={(v) => setNumeric("angle_flat", v)}
                    />
                    <NumField
                      label="Ângulo — baixa inclinação (°)"
                      value={getValue("angle_low")}
                      onChange={(v) => setNumeric("angle_low", v)}
                    />
                    <NumField
                      label="Ângulo — alta inclinação (°)"
                      value={getValue("angle_high")}
                      onChange={(v) => setNumeric("angle_high", v)}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="margins">
              <Card>
                <CardHeader>
                  <CardTitle>Margens, IVA & Desconto (Solar)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Margem default (multiplicador)"
                      step="0.0001"
                      helper="Fallback quando a marca não tem margem definida ou está a 0. Ex.: 1.65 = +65% sobre o custo."
                      value={getValue("default_margin")}
                      onChange={(v) => setNumeric("default_margin", v)}
                    />
                    <NumField
                      label="Multiplicador IVA"
                      step="0.0001"
                      helper="Ex.: 1.23 para IVA a 23%"
                      value={getValue("vat_multiplier")}
                      onChange={(v) => setNumeric("vat_multiplier", v)}
                    />
                    <NumField
                      label="Desconto máximo (fracção)"
                      step="0.0001"
                      helper="0 = sem limite (qualquer fracção 0–1). >0 = cap (ex.: 0.15 = máx. 15%)."
                      value={getValue("max_discount_fraction")}
                      onChange={(v) => setNumeric("max_discount_fraction", v)}
                    />
                    <NumField
                      label="Desconto automático: a partir de N baterias"
                      step="1"
                      helper="Propostas com este nº de baterias ou mais levam o desconto abaixo (só as criadas depois desta versão; nunca os kits do site)."
                      value={getValue("battery_discount_min_qty") ?? 2}
                      onChange={(v) => setNumeric("battery_discount_min_qty", v)}
                    />
                    <NumField
                      label="Desconto automático por baterias (fracção)"
                      step="0.01"
                      helper="Sobre o valor final, soma ao desconto do comercial (não conta para o desconto máximo). Ex.: 0.10 = 10%. 0 = desligado."
                      value={getValue("battery_discount_fraction") ?? 0.1}
                      onChange={(v) => setNumeric("battery_discount_fraction", v)}
                    />
                    <NumField
                      label="Fracção de IVA aplicada"
                      step="0.0001"
                      helper="1.0 = IVA total (23%, default). 0.80 = IVA reduzido (~18.4%, regime habitação/eficiência). 0.0 = isenção total."
                      value={getValue("tax_exemption_factor")}
                      onChange={(v) => setNumeric("tax_exemption_factor", v)}
                    />
                  </Grid>
                  <Separator className="my-4" />
                  <div className="flex items-start justify-between gap-4">
                    <div className="pr-4">
                      <Label htmlFor="allow-seller-discount">
                        Permitir desconto a vendedores
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Quando ativo, os Cargos não-admin (ex.: Vendedor) também
                        podem aplicar desconto no Editar Kit. Desligado, só
                        Administrador/Master descontam. O aumento de preço está
                        sempre disponível para todos.
                      </p>
                    </div>
                    <Switch
                      id="allow-seller-discount"
                      checked={getValue("allow_seller_discount") === true}
                      onCheckedChange={(v) =>
                        setDraft((d) => ({ ...d, allow_seller_discount: v }))
                      }
                    />
                  </div>
                  <Separator className="my-4" />
                  <div className="flex items-start justify-between gap-4">
                    <div className="pr-4">
                      <Label htmlFor="odoo-single-line">
                        Odoo: linha única negociada
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Ao enviar para o Odoo, o valor do kit <b>sem IVA</b> vai na
                        1ª linha (produto de fornecimento) e os componentes vão
                        todos a 0 (valor negociado em bloco). Desligado, cada linha
                        vai com o seu preço.
                      </p>
                    </div>
                    <Switch
                      id="odoo-single-line"
                      checked={getValue("odoo_negotiated_single_line") === true}
                      onCheckedChange={(v) =>
                        setDraft((d) => ({ ...d, odoo_negotiated_single_line: v }))
                      }
                    />
                  </div>
                  {getValue("odoo_negotiated_single_line") === true && (
                    <div className="mt-3 flex items-center gap-3">
                      <Label htmlFor="odoo-supply-code" className="text-xs">
                        Código Odoo (default_code) da linha de fornecimento
                      </Label>
                      <Input
                        id="odoo-supply-code"
                        value={(getValue("odoo_supply_product_code") as string | null) ?? ""}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            odoo_supply_product_code: e.target.value || null,
                          }))
                        }
                        placeholder="PROD981"
                        className="h-8 w-40"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="summer">
              <Card>
                <CardHeader>
                  <CardTitle>Período de Verão</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Mês de início (1..12)"
                      value={getValue("summer_start_month")}
                      onChange={(v) => setNumeric("summer_start_month", v)}
                    />
                    <NumField
                      label="Mês de fim (1..12)"
                      value={getValue("summer_end_month")}
                      onChange={(v) => setNumeric("summer_end_month", v)}
                    />
                    <NumField
                      label="Factor consumo vazio no verão"
                      step="0.0001"
                      value={getValue("summer_empty_consumption_factor")}
                      onChange={(v) =>
                        setNumeric("summer_empty_consumption_factor", v)
                      }
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sell">
              <Card>
                <CardHeader>
                  <CardTitle>Preços de venda à rede (€/kWh)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Vazio"
                      step="0.0001"
                      value={getValue("sell_price_empty_eur_kwh")}
                      onChange={(v) => setNumeric("sell_price_empty_eur_kwh", v)}
                    />
                    <NumField
                      label="Ponta"
                      step="0.0001"
                      value={getValue("sell_price_peak_eur_kwh")}
                      onChange={(v) => setNumeric("sell_price_peak_eur_kwh", v)}
                    />
                    <NumField
                      label="Cheias"
                      step="0.0001"
                      value={getValue("sell_price_full_eur_kwh")}
                      onChange={(v) => setNumeric("sell_price_full_eur_kwh", v)}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>


            <TabsContent value="tariffs">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between gap-2">
                      Distribuição geral de tarifas (%)
                      {(() => {
                        const sum = Math.round(
                          ((Number(getValue("default_consumption_empty_fraction")) || 0) +
                            (Number(getValue("default_consumption_peak_fraction")) || 0) +
                            (Number(getValue("default_consumption_full_fraction")) || 0)) *
                            100,
                        )
                        return (
                          <span
                            className={cn(
                              "rounded px-2 py-0.5 text-xs font-semibold",
                              sum === 100
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                                : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
                            )}
                          >
                            Total: {sum}%
                          </span>
                        )
                      })()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-3 text-xs text-muted-foreground">
                      Repartição do consumo por período (Vazio · Ponta · Cheias).
                      Alimenta o gráfico de consumos e o dimensionamento: com
                      bateria os painéis têm de suportar o consumo em Ponta e
                      Cheias. A soma tem de dar 100%.
                    </p>
                    <Grid>
                      <NumField
                        label="Vazio (%)"
                        step="1"
                        value={pctOf("default_consumption_empty_fraction")}
                        onChange={(v) => setPct("default_consumption_empty_fraction", v)}
                      />
                      <NumField
                        label="Ponta (%)"
                        step="1"
                        value={pctOf("default_consumption_peak_fraction")}
                        onChange={(v) => setPct("default_consumption_peak_fraction", v)}
                      />
                      <NumField
                        label="Cheias (%)"
                        step="1"
                        value={pctOf("default_consumption_full_fraction")}
                        onChange={(v) => setPct("default_consumption_full_fraction", v)}
                      />
                    </Grid>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Tarifa simples (€/kWh)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Grid>
                      <NumField
                        label="Preço único"
                        step="0.0001"
                        value={getValue("tariff_simple_price_eur_kwh")}
                        onChange={(v) =>
                          setNumeric("tariff_simple_price_eur_kwh", v)
                        }
                      />
                    </Grid>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Tarifa bi-horária (€/kWh)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Grid>
                      <NumField
                        label="Vazio"
                        step="0.0001"
                        value={getValue("tariff_bi_empty_eur_kwh")}
                        onChange={(v) => setNumeric("tariff_bi_empty_eur_kwh", v)}
                      />
                      <NumField
                        label="Fora Vazio"
                        step="0.0001"
                        value={getValue("tariff_bi_peak_eur_kwh")}
                        onChange={(v) => setNumeric("tariff_bi_peak_eur_kwh", v)}
                      />
                    </Grid>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Tarifa tri-horária (€/kWh)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Grid>
                      <NumField
                        label="Vazio"
                        step="0.0001"
                        value={getValue("tariff_tri_empty_eur_kwh")}
                        onChange={(v) => setNumeric("tariff_tri_empty_eur_kwh", v)}
                      />
                      <NumField
                        label="Ponta"
                        step="0.0001"
                        value={getValue("tariff_tri_peak_eur_kwh")}
                        onChange={(v) => setNumeric("tariff_tri_peak_eur_kwh", v)}
                      />
                      <NumField
                        label="Cheias"
                        step="0.0001"
                        value={getValue("tariff_tri_full_eur_kwh")}
                        onChange={(v) => setNumeric("tariff_tri_full_eur_kwh", v)}
                      />
                    </Grid>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="pvgis">
              <Card>
                <CardHeader>
                  <CardTitle>PVGIS</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <NumField
                      label="Perdas do sistema (%)"
                      step="0.01"
                      value={getValue("pvgis_loss_pct")}
                      onChange={(v) => setNumeric("pvgis_loss_pct", v)}
                    />
                    <StrField
                      label="Tecnologia do painel (pvtechchoice)"
                      helper="Ex.: crystSi, CIS, CdTe"
                      value={getValue("pvgis_pvtechchoice") ?? ""}
                      onChange={(v) => setString("pvgis_pvtechchoice", v)}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="brands">
              <Card>
                <CardHeader>
                  <CardTitle>Marcas Solar</CardTitle>
                </CardHeader>
                <CardContent>
                  <BrandMarginsTab shell={shell} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="categories">
              <Card>
                <CardHeader>
                  <CardTitle>Categorias → Roles Solar</CardTitle>
                </CardHeader>
                <CardContent>
                  <CategoryRolesTab shell={shell} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="powers">
              <Card>
                <CardHeader>
                  <CardTitle>Potências Contratadas</CardTitle>
                </CardHeader>
                <CardContent>
                  <ContractedPowersTab shell={shell} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="autogen">
              <Card>
                <CardHeader>
                  <CardTitle>Auto-geração de simulações</CardTitle>
                </CardHeader>
                <CardContent>
                  <Grid>
                    <StrField
                      label="Tag de lead que dispara auto-criação"
                      helper='Ex.: "simular-solar". Vazio = desactivado.'
                      value={getValue("auto_create_tag") ?? ""}
                      onChange={(v) => setString("auto_create_tag", v)}
                    />
                    <NumField
                      label="Valor médio mensal default (€)"
                      helper="Usado quando a lead não tem valor de fatura"
                      value={getValue("auto_create_default_bill_eur")}
                      onChange={(v) => setNumeric("auto_create_default_bill_eur", v)}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============================================================ */}
        {/* AC                                                              */}
        {/* ============================================================ */}
        <TabsContent value="ac">
          <ModuleConfigCard
            label={KIND_LABEL_PT.ac}
            config={getModuleConfig("ac_config")}
            onPatch={(p) => patchModuleConfig("ac_config", p)}
            sizing={{ defaultW: 120, help: "Por divisão: área × W/m² → BTU (1 W = 3,412 BTU/h) → escalão 7 000 / 9 000 / 12 000 / 18 000 / 24 000." }}
          />
        </TabsContent>

        {/* ============================================================ */}
        {/* BC AQS                                                          */}
        {/* ============================================================ */}
        <TabsContent value="bc_aqs">
          <ModuleConfigCard
            label={KIND_LABEL_PT.bc_aqs}
            config={getModuleConfig("bc_aqs_config")}
            onPatch={(p) => patchModuleConfig("bc_aqs_config", p)}
          />
        </TabsContent>

        {/* ============================================================ */}
        {/* BC AQ                                                           */}
        {/* ============================================================ */}
        <TabsContent value="bc_aq">
          <ModuleConfigCard
            label={KIND_LABEL_PT.bc_aq}
            config={getModuleConfig("bc_aq_config")}
            onPatch={(p) => patchModuleConfig("bc_aq_config", p)}
            sizing={{ defaultW: 100, help: "Área aquecida × W/m² → potência da bomba de calor (mínimo 4 kW)." }}
          />
        </TabsContent>
        {shell?.isAdmin && (
          <TabsContent value="site_kits">
            <SiteKitsTab shell={shell} />
          </TabsContent>
        )}
        {shell?.isAdmin && (
          <TabsContent value="areas">
            <AreaAccessTab shell={shell} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

/**
 * Parâmetros de um módulo (settings.<kind>_config). Só o que o motor usa:
 * a potência por m² do dimensionamento (AC e bomba de calor de aquecimento).
 */
function ModuleConfigCard({
  label,
  config,
  onPatch,
  sizing,
}: {
  label: string
  config: ModuleConfig
  onPatch: (p: Partial<ModuleConfig>) => void
  /** Sem dimensionamento por área (ex.: AQS, pelo nº de ocupantes) → omitir. */
  sizing?: { defaultW: number; help: string }
}) {
  const wRaw = config.w_per_m2
  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sizing ? (
          <Grid>
            <div className="space-y-1">
              <Label>Potência por m² (W/m²)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                placeholder={String(sizing.defaultW)}
                value={wRaw == null ? "" : String(wRaw)}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === "") onPatch({ w_per_m2: null })
                  else {
                    const n = Number(v)
                    if (Number.isFinite(n) && n > 0) onPatch({ w_per_m2: n })
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                {sizing.help} Vazio = {sizing.defaultW} W/m². Aplica-se às simulações novas e às
                que forem recalculadas.
              </p>
            </div>
          </Grid>
        ) : (
          <p className="text-xs text-muted-foreground">
            Sem parâmetros de dimensionamento: o depósito é escolhido pelo número de ocupantes.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  )
}

/**
 * Marca escolhida pelo nome (as do separador Marcas) — guarda o brand_id.
 * Antes era um campo de número e era preciso saber o id.
 */
function BrandSelectField({
  shell,
  label,
  helper,
  value,
  onChange,
}: {
  shell: AppShell | null
  label: string
  helper?: string
  value: number | string | null | undefined
  onChange: (v: string) => void
}) {
  const [brands, setBrands] = useState<BrandRow[]>([])
  useEffect(() => {
    if (!shell) return
    listBrands(shell)
      .then((rows) =>
        setBrands(
          rows
            .filter((b) => b.is_active !== false)
            .sort((a, b) => (a.solar_sort_order ?? 999) - (b.solar_sort_order ?? 999) || a.brand_name.localeCompare(b.brand_name)),
        ),
      )
      .catch(() => setBrands([]))
  }, [shell])
  const current = value == null || value === "" ? "" : String(value)
  const known = brands.some((b) => String(b.brand_id) === current)
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select value={current || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger>
          <SelectValue placeholder="Escolher marca…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Nenhuma</SelectItem>
          {current && !known && <SelectItem value={current}>Marca #{current} (não está nas Marcas)</SelectItem>}
          {brands.map((b) => (
            <SelectItem key={b.brand_id} value={String(b.brand_id)}>
              {b.brand_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  )
}

function NumField({
  label,
  value,
  helper,
  step,
  onChange,
}: {
  label: string
  value: number | string | null | undefined
  helper?: string
  step?: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        type="number"
        step={step ?? "0.01"}
        value={value == null ? "" : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  )
}

function StrField({
  label,
  value,
  helper,
  onChange,
}: {
  label: string
  value: string
  helper?: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
      {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
    </div>
  )
}
